import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { FeedbackTriage } from "@/components/FeedbackTriage";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/cn";
import { LABELS, useSubmissions } from "@/lib/admin";
import { fmtDate } from "@/lib/briefs";
import { useAllFeedback } from "@/lib/feedback";

const pill = (active: boolean) =>
  cn("rounded-full px-3 py-1 text-xs", active ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground hover:text-foreground");

/**
 * The managers' page: the queue of submitted briefs (SPEC §6) and, on a
 * second tab, feedback triage (SPEC §8). The tab lives in the URL
 * (`?tab=feedback`) because the feedback email links straight to it.
 */
export function Submissions() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "feedback" ? "feedback" : "briefs";
  // Only the count: the list itself is fetched by the tab when it is shown.
  const newFeedback = useAllFeedback({ status: "New", category: "" }).data?.newCount ?? 0;

  const tabClass = (active: boolean) =>
    cn("-mb-px border-b-2 px-1 pb-2 text-sm", active ? "border-primary font-medium" : "text-muted-foreground border-transparent hover:text-foreground");

  return (
    <main className="mx-auto max-w-5xl px-4 py-7">
      <div className="border-border mb-5 flex gap-5 border-b" role="tablist">
        <button role="tab" aria-selected={tab === "briefs"} className={tabClass(tab === "briefs")} onClick={() => setParams({})}>
          Submissions
        </button>
        <button role="tab" aria-selected={tab === "feedback"} className={tabClass(tab === "feedback")} onClick={() => setParams({ tab: "feedback" })}>
          Feedback{newFeedback ? ` (${newFeedback})` : ""}
        </button>
      </div>
      {tab === "feedback" ? (
        <>
          <p className="text-muted-foreground mb-4 text-sm">
            What people sent from the feedback button. Setting a status or replying tells them in-app and by email.
          </p>
          <FeedbackTriage />
        </>
      ) : (
        <Queue />
      )}
    </main>
  );
}

function Queue() {
  const [label, setLabel] = useState("");
  const [q, setQ] = useState("");
  const data = useSubmissions({ label, q });

  const counts = data.data?.counts;

  return (
    <>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Submissions</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Briefs as their authors sent them. A label is information for both sides; it gates nothing.
          </p>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, summary or author" className="w-64" />
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        <button className={pill(label === "")} onClick={() => setLabel("")}>
          All{counts ? ` (${counts.all})` : ""}
        </button>
        <button className={pill(label === "unlabelled")} onClick={() => setLabel("unlabelled")}>
          Unlabelled{counts ? ` (${counts.unlabelled})` : ""}
        </button>
        {LABELS.map((l) => (
          <button key={l} className={pill(label === l)} onClick={() => setLabel(l)}>
            {l}
            {counts ? ` (${counts.byLabel[l]})` : ""}
          </button>
        ))}
      </div>

      {data.isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : data.isError ? (
        <p className="text-destructive text-sm">Could not load submissions.</p>
      ) : data.data.submissions.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing here yet.</p>
      ) : (
        <ul className="divide-border bg-card divide-y rounded-lg border">
          {data.data.submissions.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
              <div className="min-w-0 flex-1">
                <Link to={`/briefs/${s.id}`} className="block truncate font-medium hover:underline">
                  {s.title}
                </Link>
                <div className="text-muted-foreground truncate text-xs">
                  {s.ownerName ? `${s.ownerName} · ` : ""}
                  {s.ownerEmail}
                  {s.summary ? ` · ${s.summary}` : ""}
                </div>
              </div>
              <div className="text-muted-foreground flex shrink-0 items-center gap-3 text-xs">
                <span>
                  v{s.submissionCount}
                  {s.editedSinceSubmit ? " · edited since" : ""}
                </span>
                <span>{fmtDate(s.lastSubmittedAt)}</span>
                {s.archivedAt ? <span>archived by author</span> : null}
                <span className={cn("rounded-full px-2 py-0.5", s.adminLabel ? "bg-accent text-accent-foreground" : "bg-secondary")}>
                  {s.adminLabel ?? "Unlabelled"}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
